import type { ButtonHTMLAttributes } from "react";
import { forwardRef, Fragment, useCallback, useState } from "react";

import styled from "styled-components";

import { SWReactCountryIcons, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setElevation, setFontVariant } from "@similarweb/styles";
import type {
    IButtonProps } from "@similarweb/ui-components";
import {
    Button,
    CheckboxIcon,
    MultiSelectCountryDropdownItem,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@similarweb/ui-components";
import { Text, Flex, Box } from "@similarweb/ui-components/foundations";

import {
    useCurrentCampaignLocations,
    useMonitorAndProtectCurrentCampaign,
} from "app/pages/digital-marketing/monitor-and-protect/services/api/campaign";
import { useCountries } from "app/pages/digital-marketing/monitor-and-protect/services/country/use-countries";
import { headerTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/header";
import { useTranslation } from "pro-features/components/WithTranslation";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useMonitorAndProtectDataAndParams } from "../../../hooks/navigations";
import type { IFilter } from "../FiltersBar";

const CountryDropDownItemStyled = styled(MultiSelectCountryDropdownItem)`
    ${setFontVariant("input2", colorsPalettes.carbon[500])};
    padding: 10px 16px;

    ${({ selected }) => {
        if (selected) {
            return `
                background-color: ${colorsPalettes.bluegrey[100]};
            `;
        }
    }}
`;

const ApplyButton = styled((props) => (
    <Button
        width="100%"
        {...props}
    />
))<IButtonProps>`
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 0 12px;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    background-color: ${colorsPalettes.blue[400]};
    text-align: right;
    color: #fff;
    width: 100%;
    border-radius: 0px;
    height: 44px !important;
    > div {
        flex: 0 0 auto;
    }

    ${({ isDisabled }) => {
        if (isDisabled) {
            return `
                background-color: ${colorsPalettes.carbon["200"]};
                cursor: not-allowed;
            `;
        }
        return "";
    }}
`;

const PopoverContentStyled = styled(PopoverContent)`
    ${setElevation("Z2")};
    padding: 0;
    border-radius: 3px;
`;

const LocationSelectAllOption = styled.div<{ $selected?: boolean }>`
    box-sizing: border-box;
    padding: 10px 16px;
    display: flex;
    gap: 8px;
    align-items: center;
    height: 44px;
    justify-content: space-between;

    ${({ $selected }) => {
        return (
            $selected &&
            `
            background-color: ${colorsPalettes.bluegrey[100]};
            `
        );
    }}
`;

type CountryObjectList = Array<{ countryCode: number; title: string }>;

interface ICountryDropDownTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    countryObjectList: CountryObjectList;
    currentSelectedItems?: number[];
}

const CountryDropDownTriggerButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 12px;
    height: 60px;
    min-width: 200px;
    background: transparent;
    border: none;
    color: ${colorsPalettes.carbon["300"]};

    &:hover:not(:disabled) {
        cursor: pointer;
        background-color: ${colorsPalettes.bluegrey[100]};
        color: ${colorsPalettes.midnight[400]};

        .CountryDropDownTriggerButton-icon svg path {
            fill: ${colorsPalettes.blue[400]};
        }
    }

    &:disabled {
        cursor: default;
        color: ${colorsPalettes.carbon[200]};

        .CountryDropDownTriggerButton-icon svg path {
            fill: ${colorsPalettes.carbon[200]};
        }

        .CountryDropDownTriggerButton-content svg path {
            opacity: 0.5;
        }
    }
`;
const CountryDropDownTrigger = forwardRef<HTMLButtonElement, ICountryDropDownTriggerProps>(
    ({ countryObjectList, currentSelectedItems = [], color, ...rest }, ref) => {
        const selectedLocationsCount = currentSelectedItems.length;
        const popoverTriggerText = selectedLocationsCount === 0 ? "" : `${selectedLocationsCount} Locations`;
        const singleLocationsCampaign = countryObjectList.length === 1;
        const isButtonDisabled = singleLocationsCampaign;
        return (
            <CountryDropDownTriggerButton
                ref={ref}
                data-automation="locations-filter-dropdown-button"
                disabled={isButtonDisabled}
                {...rest}
            >
                <Flex
                    gap="8px"
                    alignItems="center"
                    className="CountryDropDownTriggerButton-content"
                >
                    {singleLocationsCampaign || selectedLocationsCount === 1 ? (
                        <Fragment>
                            <SWReactCountryIcons
                                countryCode={
                                    singleLocationsCampaign ? countryObjectList[0].countryCode : currentSelectedItems[0]
                                }
                                size="24px"
                            />
                            <Text fontVariant="input2">
                                {countryObjectList.find((location) => location.countryCode === currentSelectedItems[0])
                                    ?.title || "Unknown Location"}
                            </Text>
                        </Fragment>
                    ) : (
                        <Fragment>
                            <SWReactIcons
                                iconName="global-rank"
                                size="24px"
                            />
                            <Text fontVariant="input2">{popoverTriggerText}</Text>
                        </Fragment>
                    )}
                </Flex>
                <Box
                    alignSelf="center"
                    margin="0 0 4px"
                >
                    <SWReactIcons
                        className="CountryDropDownTriggerButton-icon"
                        iconName="arrow"
                    />
                </Box>
            </CountryDropDownTriggerButton>
        );
    },
);

const LocationsFilterItem = () => {
    const translate = useTranslation();
    const campaignLocations = useCurrentCampaignLocations();
    const monitorAndProtectParams = useMonitorAndProtectDataAndParams();
    const { getCountryById } = useCountries();

    const getDefaultActiveLocations = useCallback(() => {
        if (monitorAndProtectParams.location.length === 0) {
            return campaignLocations.map((location) => location.countryCode);
        }

        return monitorAndProtectParams.location;
    }, [campaignLocations, monitorAndProtectParams.location]);

    const [currentLocations, setCurrentLocations] = useState<number[]>(getDefaultActiveLocations());
    const [isOpen, setIsOpen] = useState(false);

    const handleSetCurrentLocations = (clb: number[] | ((locations: number[]) => number[])) => {
        setCurrentLocations(clb);
    };

    const handlePopOverChange = (open: boolean) => {
        setIsOpen(open);

        // We don't know when the event is triggered because of clicking outside the popover or clicking on the trigger,
        // so we need to reset the selected state only when the popover is closed.
        if (open) {
            return;
        }

        setCurrentLocations(getDefaultActiveLocations());
    };

    const handleChangeCountry = (countryId: number) => {
        handleSetCurrentLocations((prevLocations) => {
            const newLocations = prevLocations.includes(countryId)
                ? prevLocations.filter((id) => id !== countryId)
                : [...prevLocations, countryId];

            return newLocations;
        });
    };

    const handleSelectAllLocations = () => {
        if (campaignLocations.length === currentLocations.length) {
            handleSetCurrentLocations([]);
        } else {
            handleSetCurrentLocations(campaignLocations.map((location) => location.countryCode));
        }
    };

    const handleApplyChanges = () => {
        if (campaignLocations.length === currentLocations.length) {
            // If all locations are selected, clear the location filter
            proNavigator.updateQueryParams(
                {
                    location: null,
                },
                { reload: false },
            );
        } else {
            // Apply partial selected state
            proNavigator.updateQueryParams(
                {
                    location: currentLocations,
                },
                { reload: false },
            );
        }

        headerTracking.filterCountrySelect(
            currentLocations
                .map((location) => campaignLocations.find((c) => c.countryCode === location).countryCode)
                .filter(Boolean),
        );

        setIsOpen(false);
    };

    return (
        <Popover
            placement="bottom-start"
            open={isOpen}
            onOpenChange={handlePopOverChange}
            offset={1}
        >
            <PopoverTrigger asChild>
                <CountryDropDownTrigger
                    countryObjectList={campaignLocations.map((location) => ({
                        countryCode: location.countryCode,
                        title: getCountryById(location.countryCode)?.text ?? "",
                    }))}
                    currentSelectedItems={getDefaultActiveLocations()}
                />
            </PopoverTrigger>
            <PopoverContentStyled>
                <Flex
                    flexDirection="column"
                    alignItems="stretch"
                    minWidth="240px"
                    maxHeight="290px"
                    overflowY="auto"
                    data-automation="locations-filter-dropdown"
                >
                    <LocationSelectAllOption
                        onClick={handleSelectAllLocations}
                        $selected={currentLocations.length > 0}
                    >
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("global.all_countries")}
                        </Text>
                        <CheckboxIcon
                            selected={campaignLocations.length === currentLocations.length}
                            halfSelected={
                                currentLocations.length > 0 ? currentLocations.length < campaignLocations.length : null
                            }
                        />
                    </LocationSelectAllOption>
                    <hr style={{ margin: 0 }} />
                    {campaignLocations.map((location) => {
                        const isSelected = currentLocations.includes(location.countryCode);
                        return (
                            <CountryDropDownItemStyled
                                key={location.countryCode}
                                countryIconID={location.countryCode}
                                text={getCountryById(location.countryCode)?.text ?? ""}
                                selected={isSelected}
                                active={isSelected}
                                onClick={() => handleChangeCountry(location.countryCode)}
                            />
                        );
                    })}
                </Flex>
                <ApplyButton
                    isDisabled={currentLocations.length === 0}
                    onClick={handleApplyChanges}
                >
                    {translate("global.apply")}
                </ApplyButton>
            </PopoverContentStyled>
        </Popover>
    );
};

// This wrapper is used to reset the filter state when the component is re-mounted
// This is to fix issue when we need to close the filter dropdown after applying changes
// and the component is not re-mounted, so the state is not reset.
const LocationsFilterConfigResetWrapper = () => {
    const monitorAndProtectParams = useMonitorAndProtectDataAndParams();
    const { data: currentCampaign } = useMonitorAndProtectCurrentCampaign();

    if (!currentCampaign) {
        return null;
    }

    return <LocationsFilterItem key={`${currentCampaign?.id}-${monitorAndProtectParams?.location?.join("-")}`} />;
};

export const locationsFilterConfigItem: IFilter = {
    id: "locations",
    filter: <LocationsFilterConfigResetWrapper />,
};
